import React, { useEffect, useMemo, useRef } from 'react';
import { useMonitorTiming } from '@melio/ap-activities';
import { getIsRppsVendor, getVendorAccountNumber } from '@melio/ap-widgets';
import { Container, Group, Loader, OnSubmissionStateChange } from '@melio/penny';
import {
  BillSubscription,
  UpdateVendorParams,
  useDeliveryMethods,
  useUnilateralRequests,
  VendorEBillStatusEnum,
} from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMonitoring } from '@melio/platform-monitoring';

import { useRouter } from '@/hooks/router.hooks';
import { useIsInternationalSupportForBusiness } from '@/hooks/vendorInternational.hooks';
import { useVendorEnrichedByIdQuery } from '@/hooks/vendors.hooks';
import { VendorDetailsFormFields } from '@/types/vendors.types';
import { RecurringPayments } from '@/widgets/vendorsDashboard/RecurringPayments/RecurringPayments.widget';
import { TaxPayerInfoCard } from '@/widgets/vendorsDashboard/TaxPayerInfo/TaxPayerInfoCard.widget';
import { VendorDrawerDeliveryMethods } from './components/VendorDrawerDeliveryMethods/VendorDrawerDeliveryMethods';
import { VendorDrawerDetails, VendorDrawerDetailsHandler } from './components/VendorDrawerDetails/VendorDrawerDetails';
import {
  VendorDrawerPaymentsOverview,
  VendorDrawerPaymentsOverviewHandler,
} from './components/VendorDrawerPaymentsOverview/VendorDrawerPaymentsOverview';
import { VendorEBillSection } from './components/VendorEBillSection/VendorEBillSection';

type VendorDrawerBodyProps = {
  vendorId?: string;
  onSubmissionStateChange: OnSubmissionStateChange<VendorDetailsFormFields>;
  onSubmit: (data: UpdateVendorParams) => void;
  isEditMode: boolean;
  onAutoPaymentCancellation: VoidFunction;
  onCancelEBillSubscription: VoidFunction;
  onEditBillSubscription: ({ id }: { id: string }) => void;
};

export const VendorDrawerBody = ({
  vendorId,
  onSubmissionStateChange,
  onSubmit,
  isEditMode,
  onCancelEBillSubscription,
  onAutoPaymentCancellation,
  onEditBillSubscription,
}: VendorDrawerBodyProps) => {
  const [isEbillsFFEnabled] = useFeature(FeatureFlags.EBills, false);
  const { generateNPEDashboardLink } = useRouter();
  const { isInternationalSupportedForBusiness } = useIsInternationalSupportForBusiness();
  const VendorPaymentsOverviewRef = React.useRef<VendorDrawerPaymentsOverviewHandler>(null);
  const { data: vendor, isLoading: isVendorLoading, isMutating } = useVendorEnrichedByIdQuery(vendorId);
  const { data: deliveryMethods = [], isLoading: isDeliveryMethodsLoading } = useDeliveryMethods({
    vendorId: vendorId,
  });

  const { isLoading: areUnilateralRequestsLoading } = useUnilateralRequests({
    vendorId: vendorId!,
    enabled: !!vendorId,
  });

  const vendorDetailsRef = useRef<VendorDrawerDetailsHandler>(null);

  useEffect(() => {
    if (isEditMode) {
      vendorDetailsRef.current?.scrollIntoView();
    }
  }, [isEditMode]);

  const defaultValues = useMemo(() => {
    return {
      companyName: vendor?.name! || '',
      accountNumber: vendor && getVendorAccountNumber(vendor),
      address: vendor?.contact.address || null,
      fullName: vendor?.contact.name || '',
      email: vendor?.contact.email || '',
      phone: vendor?.contact.phoneNumber || '',
      nickname: vendor?.nickname || '',
    };
  }, [vendor]);

  const isRppsVendor = vendor && getIsRppsVendor(vendor);
  const [isW9TaxPayerInfoEnabled] = useFeature(FeatureFlags.W9TaxPayerInfo, false);

  const isEligibleForW9 = isW9TaxPayerInfoEnabled && !vendor?.isManaged;

  const isLoading = !vendor || isVendorLoading || isDeliveryMethodsLoading || areUnilateralRequestsLoading;
  const legacyRouteReady = useMonitorTiming('vendor_drawer_ready');
  const { routeReady } = useMonitoring();
  const triggerMonitoring = (el: HTMLDivElement) => {
    legacyRouteReady(el);
    routeReady(el);
  };

  if (isLoading) {
    return <Loader />;
  }

  const billSubscriptions =
    (vendor?.billSubscriptions?.filter(
      (billSubscription) =>
        billSubscription.nextOccurrence && ['created', 'in-progress', 'done'].includes(billSubscription.status),
    ) as WithRequiredProperty<BillSubscription, 'nextOccurrence'>[]) ?? [];

  return (
    <Container ref={triggerMonitoring}>
      <Group variant="vertical" spacing="l" hasDivider>
        {isEbillsFFEnabled && vendor.eBillStatus !== VendorEBillStatusEnum.NotCapable && (
          <VendorEBillSection
            vendor={vendor}
            onAutoPaymentCancellation={onAutoPaymentCancellation}
            onCancelEBillSubscription={onCancelEBillSubscription}
          />
        )}
        <VendorDrawerPaymentsOverview
          ref={VendorPaymentsOverviewRef}
          vendor={vendor}
          vendorBills={[]}
          linkToUnpaid={generateNPEDashboardLink(`bills?search=${encodeURIComponent(vendor.name)}`)}
          linkToScheduled={generateNPEDashboardLink(
            `payments?status=scheduled&search=${encodeURIComponent(vendor.name)}`,
          )}
          linkToPaid={generateNPEDashboardLink(`payments?status=paid&search=${encodeURIComponent(vendor.name)}`)}
          isInternationalSupportedForBusiness={isInternationalSupportedForBusiness}
        />
        {vendor && billSubscriptions.length > 0 ? (
          <RecurringPayments
            billSubscriptions={billSubscriptions}
            vendor={vendor}
            onEditBillSubscription={onEditBillSubscription}
          />
        ) : null}
        <VendorDrawerDeliveryMethods vendor={vendor} deliveryMethods={deliveryMethods} />
        <VendorDrawerDetails
          vendorId={vendor.id}
          ref={vendorDetailsRef}
          isEditable={isEditMode}
          onSubmit={onSubmit}
          onSubmissionStateChange={onSubmissionStateChange}
          defaultValues={defaultValues}
          isRPPSVendor={isRppsVendor}
          managedBy={vendor.managedBy}
          isSaving={isMutating}
        />
        {isEligibleForW9 ? <TaxPayerInfoCard vendor={vendor} /> : null}
      </Group>
    </Container>
  );
};
